import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { BadgeCheck, Info } from "lucide-react"
import { useCallback } from 'react';

interface ModelListProps {
  activeModels: Set<string>
  onUpdateModels: (models: string[]) => void
  models: string[]
}

function getProviderName(modelId: string): string {
  if (!modelId) return '';
  const parts = modelId.split(':');
  const provider = parts[0] || modelId;
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function getModelName(modelId: string): string {
  if (!modelId) return '';
  const parts = modelId.split(':');
  const model = parts[1] || modelId;

  if (model.startsWith("gpt-")) return model.replace("gpt-", "GPT-");
  if (model.startsWith("claude-")) return model.replace("claude-", "Claude ");
  if (model.includes("gemini")) return "Gemini";
  return model;
}

function getModelIcon(modelId: string): string {
  if (!modelId) return 'UN';
  const [provider] = modelId.split(':');
  if (!provider) return modelId.slice(0, 2).toUpperCase();
  return provider.slice(0, 2).toUpperCase()
}

function getModelColor(modelId: string): string {
  if (!modelId) return "from-gray-500/20 to-slate-500/20 hover:from-gray-500/30 hover:to-slate-500/30";
  
  const [provider] = modelId.split(':');
  if (!provider) return "from-gray-500/20 to-slate-500/20 hover:from-gray-500/30 hover:to-slate-500/30";

  switch (provider.toLowerCase()) {
    case 'openai':
      return "from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30"
    case 'anthropic':
      return "from-orange-500/20 to-red-500/20 hover:from-orange-500/30 hover:to-red-500/30"
    case 'google':
      return "from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30"
    default:
      return "from-gray-500/20 to-slate-500/20 hover:from-gray-500/30 hover:to-slate-500/30"
  }
}

export function ModelList({ activeModels, onUpdateModels, models }: ModelListProps) {
  const modelItems = models.sort((a, b) => getModelName(a).localeCompare(getModelName(b)))

  const handleToggleModel = useCallback((modelId: string) => {
    const newActiveModels = new Set(activeModels)
    if (newActiveModels.has(modelId)) {
      newActiveModels.delete(modelId)
    } else {
      newActiveModels.add(modelId)
    }
    onUpdateModels(Array.from(newActiveModels))
  }, [activeModels, onUpdateModels])

  return (
    <Card className="h-full bg-background border-r rounded-none">
      <div className="p-4 border-b bg-gradient-to-r from-secondary to-accent/50 rounded-t-none backdrop-blur-md bg-opacity-80 shadow-sm">
        <h2 className="font-bold text-lg tracking-tight">Models</h2>
        <p className="text-xs text-muted-foreground">Select which models you'd like to query</p>
      </div>
      <ScrollArea className="h-[calc(100vh-10rem)]">
        <div className="p-2 space-y-2">
          {modelItems.map((modelId) => {
            const isActive = activeModels.has(modelId)
            const provider = getProviderName(modelId)
            const gradientClass = getModelColor(modelId)
            const name = getModelName(modelId)

            return (
              <div
                key={modelId}
                className={cn(
                  "relative rounded-lg p-4 w-full cursor-pointer group transition-all transform hover:scale-[1.03]",
                  gradientClass,
                  !isActive && "opacity-60 hover:opacity-90",
                  "hover:shadow-lg hover:shadow-accent/30 duration-200 ease-out",
                  isActive && "ring-2 ring-accent ring-offset-2 ring-offset-background"
                )}
                onClick={() => handleToggleModel(modelId)}
              >
                {isActive && (
                  <span className="absolute top-2 right-2 flex items-center justify-center text-primary-foreground bg-primary/80 rounded-full p-1 shadow-sm transition-transform duration-200 group-hover:scale-110">
                    <BadgeCheck className="h-4 w-4" />
                  </span>
                )}
                <span className="text-sm font-semibold w-8 h-8 flex items-center justify-center bg-muted rounded-full">
                  {getModelIcon(modelId)}
                </span>
                <div className="flex flex-col items-start gap-0.5">
                  <span className="font-medium text-sm">
                    {name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {provider}
                  </span>
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            )
          })}
        </div>
      </ScrollArea>
    </Card>
  )
}
